import React, { useState, useEffect } from 'react';
import * as R from 'ramda';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import connect from '../utils/connect';
import sendNotification from '../actions/notifications/send';
import combineActions from '../utils/combineActions';
import Dropzone from 'react-dropzone';
import UploadModal from './UploadModal';
import uploadS3 from '../utils/uploadS3';

const isValid = file => {
    const { type } = file;
    const reg = /^image\//gim;
    if (reg.test(type)) return true;
    return false;
};

const FileUpload = ({
    files,
    setFiles,
    metadata,
    uploadType,
    user,
    sendNotification,
    isReady,
}) => {
    const { t } = useTranslation('settings');
    const [uploads, setUploads] = useState([]);
    const [currentFiles, setCurrentFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState(null);
    const [errors, setErrors] = useState([]);

    useEffect(() => {
        if (
            !currentFiles ||
            !Array.isArray(currentFiles) ||
            currentFiles.length === 0
        )
            return;
        const newFiles = [...files, ...currentFiles]
            .filter((value, index, self) => {
                return self.indexOf(value) === index;
            })
            .map(f => {
                if (currentFiles.findIndex(e => f.id === e.id) > -1)
                    return {
                        ...f,
                        justAdded: true,
                    };
                return f;
            });
        setFiles(newFiles);
    }, [currentFiles]);

    useEffect(() => {
        if (!error) return;
        setErrors([...errors, error]);
    }, [error]);

    const onDrop = async uploadingfiles => {
        const newFiles = files.map(f => {
            return { ...f, justAdded: false };
        });
        setFiles(newFiles);

        const { acceptedFiles, rejectedFiles } = uploadingfiles.reduce(
            (acc, f) => {
                if (isValid(f)) {
                    acc.acceptedFiles.push(f);
                } else {
                    acc.rejectedFiles.push(f);
                }
                return acc;
            },
            { acceptedFiles: [], rejectedFiles: [] },
        );

        if (rejectedFiles.length > 0) {
            sendNotification(
                t('clientConfiguration.fileUpload.invalidFormat'),
                { context: 'warning' },
            );
        }

        if (!acceptedFiles.length) return;
        if (acceptedFiles.some(a => !a.name)) {
            sendNotification(t('clientConfiguration.fileUpload.missingName'), {
                context: 'warning',
            });
        }

        setIsUploading(true);
        setUploads(acceptedFiles);

        const uploadedImages = await _uploadFiles(acceptedFiles);

        setCurrentFiles(uploadedImages);
    };

    const _uploadFile = (file, index) => {
        return new Promise(resolve => {
            const onSuccess = (config, uploadedFile) => {
                resolve({
                    id: config.fileName,
                    url: uploadedFile,
                    isNew: true,
                    justAdded: true,
                });
            };

            uploadS3({
                uploadType,
                file,
                metadata,
                user,
                onSuccess,
                onError: () => {
                    if (files.length == 1) {
                        setIsUploading(false);
                    }
                    setError(file);
                    sendNotification(
                        t('clientConfiguration.fileUpload.uploadError'),
                        { context: 'warning' },
                    );
                },
                onProgress: e => {
                    const progress = parseInt((e.loaded / e.total) * 100, 10);
                    setUploads(R.assocPath([index, 'progress'], progress));
                },
            });
        });
    };

    const _uploadFiles = async files => {
        const promises = files.map((file, index) => _uploadFile(file, index));
        return await Promise.all(promises);
    };

    useEffect(() => {
        if (!uploads || !uploads.length || !files || !isUploading) return;

        const confirmedUploads = uploads.filter(
            u => errors.findIndex(e => e.path === u.path) < 0,
        );

        const isDoneUploading =
            confirmedUploads.every(f => f.progress && f.progress === 100) &&
            isReady(confirmedUploads);

        if (isDoneUploading) {
            setIsUploading(false);
            setUploads([]);
            setError([]);
            setError(null);
        }
    }, [uploads, files, errors]);

    return (
        <>
            <div className="gds-flex-grid__row">
                <div className="gds-flex-grid__item gds-flex-grid__item--desktop-1 gds-flex-grid__item--tablet-1 gds-flex-grid__item--mobile-1">
                    <Dropzone onDrop={onDrop}>
                        {({ getRootProps, getInputProps }) => (
                            <div
                                className="gds-landing-pad drag-and-drop-container"
                                style={{
                                    width: '100%',
                                }}
                                {...getRootProps()}
                            >
                                <input
                                    className="gds-landing-pad__input"
                                    type="file"
                                    required=""
                                    {...getInputProps()}
                                />
                                <p className="gds-landing-pad__text gds-text--body-md -m-b-2">
                                    {t('clientConfiguration.fileUpload.dragAndDrop')}
                                </p>
                                <p className="gds-landing-pad__text">{t('clientConfiguration.fileUpload.or')}</p>
                                <button
                                    type="button"
                                    className="gds-button gds-button--sm gds-button--secondary -p-h-3"
                                >
                                    {t('clientConfiguration.fileUpload.uploadFile')}
                                </button>
                            </div>
                        )}
                    </Dropzone>
                </div>
            </div>
            {isUploading && <UploadModal uploads={uploads} errors={errors} />}
        </>
    );
};

FileUpload.propTypes = {
    uploadType: PropTypes.string,
    setFiles: PropTypes.func,
    files: PropTypes.array,
    metadata: PropTypes.object,
    user: PropTypes.object.isRequired,
    sendNotification: PropTypes.func,
    isReady: PropTypes.func,
};

export default connect(
    ['user'],
    combineActions({ sendNotification }),
)(FileUpload);
